import DocumentIcon from '@heroicons/react/24/outline/DocumentIcon';
import PencilIcon from '@heroicons/react/24/outline/PencilIcon';
import PhotoIcon from '@heroicons/react/24/outline/PhotoIcon';
import { CSSProperties, useState } from 'react';

import { Badge } from '@/components/core/Badge';
import { ButtonPanel } from '@/components/core/ButtonPanel';
import { Checkbox } from '@/components/core/Checkbox';
import { Icon } from '@/components/core/Icon';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/core/Table';
import { useTranslation } from '@/hooks/useTranslation';
import { AssetApiRecord } from '@/pages/dam/api/assets.types';
import { resolveDamCardSections } from '@/pages/dam/utils/damCardSections';
import { useDamAssetContent, useDamFieldLabel } from '@/pages/dam/utils/useDamAssetContent';
import { DataModelLayout } from '@/types/DataModel';

import { DamCardBadgeRow } from '../DamAssetCard/DamCardBadgeRow';

interface DamAssetListProps {
    records: AssetApiRecord[];
    /** The `asset_card` layout with the "Card view" selection applied (D-036). */
    layout: DataModelLayout | undefined;
    onEdit: (asset: AssetApiRecord) => void;
    isSelected?: (asset: AssetApiRecord) => boolean;
    /** Selects one row; checkboxes stay disabled while no handler is wired. */
    onSelectedChange?: (asset: AssetApiRecord, isSelected: boolean) => void;
    /** Selects every row of the page from the header checkbox. */
    onSelectPage?: (isSelected: boolean) => void;
}

/** `core/Table` draws a line between all cells; the wireframe list only separates rows and the pinned edit column. */
const tableStyle = { '--table-border': 'none' } as CSSProperties;

interface DamAssetListRowProps {
    asset: AssetApiRecord;
    layout: DataModelLayout | undefined;
    showStatus: boolean;
    isSelected: boolean;
    onSelectedChange?: (isSelected: boolean) => void;
    onEdit: () => void;
}

function DamAssetListRow({ asset, layout, showStatus, isSelected, onSelectedChange, onEdit }: DamAssetListRowProps) {
    const { t } = useTranslation();
    const [imageFailed, setImageFailed] = useState(false);
    const { isImage, imageUrl, topBadge, statusBadges, title, rows } = useDamAssetContent(asset, layout);

    return (
        <TableRow className="tw-bg-fill-primary" selected={isSelected}>
            <TableCell className="tw-w-10 tw-px-2">
                <Checkbox
                    isChecked={isSelected}
                    disabled={!onSelectedChange}
                    aria-label={t('DAM.CARD.ADD_TO_BULK')}
                    onCheckedChange={(checked) => onSelectedChange?.(checked === true)}
                />
            </TableCell>
            <TableCell className="tw-py-4">
                <div className="tw-flex tw-min-w-0 tw-items-center tw-gap-4">
                    <div className="tw-flex tw-h-12 tw-w-12 tw-shrink-0 tw-items-center tw-justify-center tw-overflow-hidden tw-rounded tw-bg-fill-secondary">
                        {imageUrl && !imageFailed ? (
                            <img src={imageUrl} alt="" loading="lazy" className="tw-h-full tw-w-full tw-object-contain" onError={() => setImageFailed(true)} />
                        ) : (
                            <Icon icon={isImage ? PhotoIcon : DocumentIcon} size="md" variant="lighter-gray" />
                        )}
                    </div>
                    <span title={title} className="tw-min-w-0 tw-max-w-xs tw-flex-1 tw-truncate tw-text-t-sm tw-text-t-primary lg:tw-text-t-base xl:tw-max-w-md">{title}</span>
                    {topBadge && (
                        <Badge type="category" variant="white" size="xl" icon={topBadge.icon} className="tw-shrink-0 tw-border tw-border-solid tw-border-tertiary">
                            {topBadge.label}
                        </Badge>
                    )}
                </div>
            </TableCell>
            {showStatus && (
                <TableCell className="tw-px-2">
                    <DamCardBadgeRow badges={statusBadges} />
                </TableCell>
            )}
            {rows.map((row) => (
                <TableCell key={row.name} className="tw-whitespace-nowrap tw-px-2 tw-text-t-sm tw-text-t-primary lg:tw-text-t-base">
                    {row.value}
                </TableCell>
            ))}
            <TableCell pinned className="tw-w-14 tw-px-2 tw-text-center">
                <ButtonPanel isDisabled={false} icon={PencilIcon} aria-label={t('DAM.CARD.EDIT')} tooltip={t('DAM.CARD.EDIT')} onClick={onEdit} />
            </TableCell>
        </TableRow>
    );
}

/**
 * List view of the library (wireframe `6392:31629`): one row per asset with the
 * `asset_card` areas as columns (D-036) — "Files" (thumbnail, title, format badge),
 * "Status" (badges below the card image), one column per `BODY` field, edit pinned right.
 */
function DamAssetList({ records, layout, onEdit, isSelected, onSelectedChange, onSelectPage }: DamAssetListProps) {
    const { t } = useTranslation();
    const fieldLabel = useDamFieldLabel();
    const sections = resolveDamCardSections(layout);
    const showStatus = sections.BOTTOM_BADGES.length > 0;
    const selectedOnPage = records.filter((asset) => isSelected?.(asset)).length;

    return (
        <div className="tw-w-full tw-overflow-x-auto">
            <Table style={tableStyle} className="tw-min-w-full">
                <TableHeader className="tw-static">
                    <TableRow>
                        <TableHead className="tw-w-10">
                            <Checkbox
                                isChecked={records.length > 0 && selectedOnPage === records.length}
                                isIndeterminate={selectedOnPage > 0 && selectedOnPage < records.length}
                                disabled={!onSelectPage}
                                aria-label={t('DAM.LIST.SELECT_PAGE')}
                                onCheckedChange={() => onSelectPage?.(selectedOnPage < records.length)}
                            />
                        </TableHead>
                        <TableHead className="tw-text-t-sm tw-font-bold lg:tw-text-t-base">{t('DAM.LIST.FILES')}</TableHead>
                        {showStatus && <TableHead className="tw-whitespace-nowrap">{t('DAM.LIST.STATUS')}</TableHead>}
                        {sections.BODY.map((attribute) => (
                            <TableHead key={attribute.name} className="tw-whitespace-nowrap">{fieldLabel(attribute)}</TableHead>
                        ))}
                        <TableHead pinned className="tw-w-14">
                            <span className="tw-sr-only">{t('DAM.CARD.EDIT')}</span>
                        </TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {records.map((asset) => (
                        <DamAssetListRow
                            key={String(asset.primaryKey__)}
                            asset={asset}
                            layout={layout}
                            showStatus={showStatus}
                            isSelected={isSelected?.(asset) ?? false}
                            onSelectedChange={onSelectedChange && ((selected) => onSelectedChange(asset, selected))}
                            onEdit={() => onEdit(asset)}
                        />
                    ))}
                </TableBody>
            </Table>
        </div>
    );
}

export { DamAssetList };
export type { DamAssetListProps };
